<script>
import { defineComponent } from 'vue'
import { ElNotification } from 'element-plus'
import moment from 'moment'

export default defineComponent({
  name: 'StateBudgetExecutionReportForm',
  props: {
    id: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      fileUploading: false,
      photoUploading: false,
      dialogVisible: false,
      activeLang: 'uz_lat',
      fileName: null,
      photoUrl: null,
      form: {
        title: { uz_lat: '', uz_cyr: '', ru: '', en: '', kr: '' },
        file_id: '',
        cover_photo_id: '',
        period_type: 'YEARLY',
        year: moment().format('YYYY'),
        quarter: null,
      },
      rules: {
        'title.uz_lat': [{ required: true, message: "Majburiy maydon (UZ)", trigger: 'blur' }],
        file_id: [{ required: true, message: "PDF fayl yuklang", trigger: 'change' }],
        cover_photo_id: [{ required: true, message: "Muqova rasmi yuklang", trigger: 'change' }],
        period_type: [{ required: true, message: "Majburiy maydon", trigger: 'change' }],
        year: [{ required: true, message: "Yilni tanlang", trigger: 'change' }],
      },
    }
  },
  computed: {
    isQuarterly() {
      return this.form.period_type === 'QUARTERLY'
    },
    baseFileUrl() {
      return process.env.VUE_APP_BASE_API_URL + 'api/v2/info/file/'
    },
  },
  methods: {
    onClick() {
      this.form = {
        title: { uz_lat: '', uz_cyr: '', ru: '', en: '', kr: '' },
        file_id: '',
        cover_photo_id: '',
        period_type: 'YEARLY',
        year: moment().format('YYYY'),
        quarter: null,
      }
      this.fileName = null
      this.photoUrl = null
      this.activeLang = 'uz_lat'
      this.dialogVisible = true
      if (this.id) {
        this.loadById()
      }
    },

    loadById() {
      this.loading = true
      this.$http
        .get(`/api/v1/state-budget-execution-reports/${this.id}`)
        .then(({ data }) => {
          this.form = {
            title: { ...data.title },
            file_id: data.file_id,
            cover_photo_id: data.cover_photo_id,
            period_type: data.period_type,
            year: String(data.year),
            quarter: data.quarter || null,
          }
          this.fileName = data.file_name || data.file_id
          this.photoUrl = data.cover_photo_id ? this.baseFileUrl + data.cover_photo_id : null
        })
        .catch(({ response }) => {
          ElNotification({ type: 'error', title: 'Xatolik', message: response?.data?.message || "Yuklashda xatolik" })
        })
        .finally(() => { this.loading = false })
    },

    onPdfChange(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.type !== 'application/pdf') {
        ElNotification({ type: 'error', title: 'Xatolik', message: "Faqat PDF fayl yuklash mumkin" })
        event.target.value = ''
        return
      }
      if (file.size / 1024 / 1024 > 20) {
        ElNotification({ type: 'error', title: 'Xatolik', message: "Fayl hajmi 20MB dan oshmasligi kerak" })
        event.target.value = ''
        return
      }
      this.fileName = file.name
      this.uploadPdf(file)
      event.target.value = ''
    },

    uploadPdf(file) {
      const formData = new FormData()
      formData.append('file', file)
      this.fileUploading = true
      this.$http
        .post('api/v2/info/file/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(({ data }) => {
          this.form.file_id = data.hashId
          this.$refs.form?.validateField('file_id', () => {})
        })
        .catch(({ response }) => {
          this.fileName = null
          ElNotification({ type: 'error', title: 'Xatolik', message: response?.data?.message || "PDF yuklashda xatolik" })
        })
        .finally(() => { this.fileUploading = false })
    },

    onPhotoChange(event) {
      const file = event.target.files[0]
      if (!file) return
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      if (!allowed.includes(file.type)) {
        ElNotification({ type: 'error', title: 'Xatolik', message: "Faqat JPG, PNG, WEBP rasm yuklash mumkin" })
        event.target.value = ''
        return
      }
      if (file.size / 1024 / 1024 > 5) {
        ElNotification({ type: 'error', title: 'Xatolik', message: "Rasm hajmi 5MB dan oshmasligi kerak" })
        event.target.value = ''
        return
      }
      this.uploadPhoto(file)
      event.target.value = ''
    },

    uploadPhoto(file) {
      const formData = new FormData()
      formData.append('file', file)
      this.photoUploading = true
      this.$http
        .post('api/v2/info/file/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(({ data }) => {
          this.form.cover_photo_id = data.hashId
          this.photoUrl = this.baseFileUrl + data.hashId
          this.$refs.form?.validateField('cover_photo_id', () => {})
        })
        .catch(({ response }) => {
          ElNotification({ type: 'error', title: 'Xatolik', message: response?.data?.message || "Rasm yuklashda xatolik" })
        })
        .finally(() => { this.photoUploading = false })
    },

    onPeriodTypeChange() {
      if (this.form.period_type === 'YEARLY') {
        this.form.quarter = null
      }
    },

    onSubmit() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        if (this.isQuarterly && !this.form.quarter) {
          ElNotification({ type: 'warning', title: 'Diqqat', message: "Chorakni tanlang" })
          return
        }
        this.loading = true
        const payload = {
          title: this.form.title,
          file_id: this.form.file_id,
          cover_photo_id: this.form.cover_photo_id,
          period_type: this.form.period_type,
          year: Number(this.form.year),
          ...(this.isQuarterly ? { quarter: this.form.quarter } : {}),
        }
        const request = this.id
          ? this.$http.put(`/api/v1/state-budget-execution-reports/${this.id}`, payload)
          : this.$http.post('/api/v1/state-budget-execution-reports', payload)

        request
          .then(() => {
            ElNotification({ type: 'success', title: 'Tabriklaymiz', message: "Muvaffaqiyatli saqlandi" })
            this.dialogVisible = false
            this.$emit('getData')
          })
          .catch(({ response }) => {
            ElNotification({ type: 'error', title: 'Xatolik', message: response?.data?.message || "Iltimos, qayta urinib ko'ring" })
          })
          .finally(() => { this.loading = false })
      })
    },
  },
})
</script>

<template>
  <el-button v-if="id" type="primary" size="small" @click="onClick">Tahrirlash</el-button>
  <el-button v-else type="primary" @click="onClick">Qo'shish</el-button>

  <el-dialog
    v-model="dialogVisible"
    append-to-body
    destroy-on-close
    :title="id ? 'Hisobotni tahrirlash' : 'Hisobot qo\'shish'"
    width="700"
  >
    <div v-loading="loading">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">

        <el-tabs v-model="activeLang">
          <el-tab-pane label="UZ" name="uz_lat">
            <el-form-item label="Sarlavha (UZ)" prop="title.uz_lat">
              <el-input v-model="form.title.uz_lat" size="large" placeholder="Sarlavhani kiriting" />
            </el-form-item>
          </el-tab-pane>
          <el-tab-pane label="UZ Krill" name="uz_cyr">
            <el-form-item label="Sarlavha (UZ Krill)" prop="title.uz_cyr">
              <el-input v-model="form.title.uz_cyr" size="large" placeholder="Sarlavhani kiriting" />
            </el-form-item>
          </el-tab-pane>
          <el-tab-pane label="RU" name="ru">
            <el-form-item label="Sarlavha (RU)" prop="title.ru">
              <el-input v-model="form.title.ru" size="large" placeholder="Sarlavhani kiriting" />
            </el-form-item>
          </el-tab-pane>
          <el-tab-pane label="EN" name="en">
            <el-form-item label="Sarlavha (EN)" prop="title.en">
              <el-input v-model="form.title.en" size="large" placeholder="Sarlavhani kiriting" />
            </el-form-item>
          </el-tab-pane>
          <el-tab-pane label="KR" name="kr">
            <el-form-item label="Sarlavha (KR)" prop="title.kr">
              <el-input v-model="form.title.kr" size="large" placeholder="Sarlavhani kiriting" />
            </el-form-item>
          </el-tab-pane>
        </el-tabs>

        <el-row :gutter="16" style="margin-top: 16px">

          <el-col :xs="24" :sm="12">
            <el-form-item label="PDF Fayl" prop="file_id">
              <el-input v-model="form.file_id" style="display: none" />
              <label class="file-upload-btn" v-loading="fileUploading">
                <input type="file" accept="application/pdf" hidden @change="onPdfChange" />
                <span v-if="fileName">{{ fileName }}</span>
                <span v-else>PDF fayl tanlang</span>
              </label>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="Muqova rasmi" prop="cover_photo_id">
              <el-input v-model="form.cover_photo_id" style="display: none" />
              <label class="photo-upload-btn" v-loading="photoUploading">
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" hidden @change="onPhotoChange" />
                <img v-if="photoUrl" :src="photoUrl" alt="cover" class="cover-preview" />
                <span v-else>Rasm tanlang</span>
              </label>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="Davr turi" prop="period_type">
              <el-select v-model="form.period_type" size="large" style="width: 100%" @change="onPeriodTypeChange">
                <el-option label="Yillik" value="YEARLY" />
                <el-option label="Choraklik" value="QUARTERLY" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item label="Yil" prop="year">
              <el-date-picker
                v-model="form.year"
                type="year"
                format="YYYY"
                value-format="YYYY"
                size="large"
                style="width: 100%"
                placeholder="Yilni tanlang"
              />
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12" v-if="isQuarterly">
            <el-form-item label="Chorak">
              <el-select v-model="form.quarter" size="large" style="width: 100%" placeholder="Chorakni tanlang">
                <el-option v-for="q in [1, 2, 3, 4]" :key="q" :label="`${q}-chorak`" :value="q" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="24">
            <el-form-item>
              <el-button @click="dialogVisible = false">Bekor qilish</el-button>
              <el-button type="primary" @click="onSubmit">Saqlash</el-button>
            </el-form-item>
          </el-col>
        </el-row>

      </el-form>
    </div>
  </el-dialog>
</template>

<style scoped>
.file-upload-btn,
.photo-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  color: #606266;
  font-size: 14px;
  width: 100%;
  min-height: 48px;
  transition: border-color 0.2s;
  box-sizing: border-box;
}
.file-upload-btn:hover,
.photo-upload-btn:hover {
  border-color: #409eff;
  color: #409eff;
}
.cover-preview {
  max-width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
